import React, { useCallback, useMemo } from 'react';
import {
    SearchOutlined,
    useBreakpoint,
    styled,
    Image,
    ClockOutlined,
} from '@landr/maestro';
import {
    ContentSuggestionParams,
    ContentSuggestionPayload,
    SuggestionsType,
    getSuggestionDisplayType,
} from 'components/Filters/context/SearchBarContext/types';
import { useLabels } from 'components/Filters/context/SearchBarContext/hooks/useLabels';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';
import { getImageUrl } from 'helpers';
import { useNavigate } from 'react-router-dom';
import { RoutePaths } from 'types/enums';
import { usePacks } from 'components/Filters/context/SearchBarContext/hooks/usePacks';

export const LabelImage = styled(Image)`
    width: 20px;
    height: 20px;
    border-radius: 2px;
`;

const useContentSuggestionItem = ({
    applySuggestion,
    createUserRecentSearch,
    deleteUserRecentSearch,
    suggestionType,
    modifiers,
    isHovered,
}: {
    applySuggestion: () => void;
    createUserRecentSearch: (modifiers: ContentSuggestionPayload[]) => void;
    deleteUserRecentSearch: (modifiers: ContentSuggestionPayload[]) => void;
    suggestionType: SuggestionsType;
    modifiers: ContentSuggestionPayload;
    isHovered: boolean;
}) => {
    const navigate = useNavigate();
    const toLocalizedPath = useToLocalizedPath();

    const [params, slug] = modifiers;
    const displayableFiltersParams = getSuggestionDisplayType();
    const filterCategories = params && displayableFiltersParams[params];

    const destinationPath = useMemo(() => {
        const page =
            params === ContentSuggestionParams.suggestedlabels
                ? RoutePaths.Labels
                : RoutePaths.Packs;

        return toLocalizedPath(`/${page}/${slug}`);
    }, [params, slug, toLocalizedPath]);

    const handleClick = useCallback(() => {
        applySuggestion();

        if (suggestionType !== SuggestionsType.Recent) {
            createUserRecentSearch([modifiers]);
        }

        navigate(destinationPath);
    }, [
        destinationPath,
        suggestionType,
        modifiers,
        applySuggestion,
        createUserRecentSearch,
        navigate,
    ]);

    const isRecentSuggestion = suggestionType === SuggestionsType.Recent;
    const isMobile = useBreakpoint('md');
    const isRemoveSuggestionVisible =
        isRecentSuggestion && (isMobile || isHovered);

    const handleRemoveRecentSuggestion = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            modifiers && deleteUserRecentSearch([modifiers]);
        },
        [deleteUserRecentSearch, modifiers],
    );

    const { labels } = useLabels();
    const { packs } = usePacks();

    const suggestedContent = useMemo(
        () =>
            params === ContentSuggestionParams.suggestedlabels
                ? labels.find((label) => label.slug === slug)
                : packs.find((pack) => pack.slug === slug),
        [slug, labels, packs, params],
    );

    const icon = isRecentSuggestion ? (
        <ClockOutlined size="sm" />
    ) : (
        <LabelImage
            src={getImageUrl({
                imageUrl: suggestedContent?.artworkUrl,
                width: 40,
            })}
            fallback={<SearchOutlined size="sm" />}
        />
    );

    const filterName = suggestedContent?.name;

    const isNavigationItem =
        suggestionType !== SuggestionsType.Recent && isHovered;

    return {
        filterName,
        icon,
        isNavigationItem,
        isRemoveSuggestionVisible,
        filterCategories,
        handleClick,
        handleRemoveRecentSuggestion,
    };
};

export default useContentSuggestionItem;
